import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { HistoryMovement } from 'types';

import { HistoryMovementsViewContextType } from '../types/HistoryMovementsViewContextType';
import { historyMovementsViewInitValue } from '../constants/historyMovementsViewInitValue';
import { useGetPaginationData } from '../hooks/useGetPaginationData';

const HistoryMovementsViewContext =
  createContext<HistoryMovementsViewContextType>(historyMovementsViewInitValue);

export const HistoryMovementsViewProvider: FC<{
  children: ReactNode;
  historyMovements: HistoryMovement[];
  operationType: string;
}> = ({ children, historyMovements, operationType }) => {
  const [expandView, setExpandView] = useState<boolean>(false);
  const [expandDirection, setExpandDirection] = useState<string>('down');
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  const { data: paginatedData } = useGetPaginationData({
    page,
    rowsPerPage,
    historyMovements,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleChangeExpandView = () => {
    setExpandView((prev) => !prev);
    setExpandDirection(expandView ? 'down' : 'up');
  };

  return (
    <HistoryMovementsViewContext.Provider
      value={{
        data: {
          expandView,
          expandDirection,
          historyMovements: paginatedData.paginatedHistoryMovements,
          totalHistoryMovements: historyMovements.length,
          page,
          rowsPerPage,
          operationType,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleChangeExpandView,
        },
      }}
    >
      {children}
    </HistoryMovementsViewContext.Provider>
  );
};

export const useHistoryMovementsView = () => {
  const context = useContext(HistoryMovementsViewContext);
  if (!context) {
    throw new Error(
      'useHistoryMovementsView must be used within a HistoryMovementsViewProvider'
    );
  }
  return context;
};
